import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Loader2, RefreshCw, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

export const AIAnnouncementGenerator = ({
  organizationId,
  audience,
  eventId,
  onGenerated,
  onClose,
}) => {
  const { session } = useAuth();
  const [tone, setTone] = useState('professional');
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  
  const generate = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setGeneratedContent('');
    
    try {
      const res = await fetch(`${API_URL}/api/ai/announcement/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          audience: audience || 'all_members',
          tone: tone,
          event_id: eventId || null,
          additional_context: additionalContext || null,
        }),
      });
      
      const data = await res.json();
      
      if (data.success) {
        setGeneratedContent(data.content);
      } else {
        toast.error(data.error || 'Failed to generate announcement');
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Failed to generate announcement');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAccept = () => {
    onGenerated(generatedContent);
    onClose();
  };
  
  return (
    <div className="bg-obsidian/50 border border-electric-ice-blue/20 rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-electric-ice-blue" />
          <span className="text-sm font-medium text-cloud-white">AI Writer</span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="text-muted-text hover:text-cloud-white -mr-2"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      {!generatedContent ? (
        <>
          <div className="space-y-2">
            <Label className="text-soft-silver text-sm">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="bg-deep-space border-white/[0.08] text-cloud-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-deep-space border-white/[0.08]">
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className="text-soft-silver text-sm">Additional Context (optional)</Label>
            <input
              type="text"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Any specific points to include..."
              className="w-full px-3 py-2 bg-deep-space border border-white/[0.08] rounded-lg text-sm text-cloud-white placeholder:text-muted-text"
            />
          </div>
          
          <Button
            onClick={generate}
            disabled={loading}
            className="w-full bg-electric-ice-blue text-obsidian hover:bg-electric-ice-blue/90"
            data-testid="ai-generate-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate
              </>
            )}
          </Button>
        </>
      ) : (
        <>
          <div className="bg-deep-space rounded-lg p-3 max-h-48 overflow-y-auto">
            <p className="text-sm text-cloud-white whitespace-pre-wrap">{generatedContent}</p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={generate}
              disabled={loading}
              variant="outline"
              className="flex-1 border-white/[0.08]"
              data-testid="ai-regenerate-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </>
              )}
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1 bg-fresh-mint text-obsidian hover:bg-fresh-mint/90"
              data-testid="ai-accept-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Accept
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAnnouncementGenerator;
